/*
 * Expected formats for the employee fields that the endorsement system checks
 * when a List 1 ticket is fixed and promoted. This is the single source of
 * truth shared by two places:
 *   - the Fix & Promote dialog, which shows the hint next to a field in error
 *   - the Docs > Field formats page, which lists every field
 *
 * The rules mirror validateCloversFields in
 * src/endorsement/src/controllers/list/listOne.controller.js. Keep them in sync.
 *
 * "validated: true" means the system rejects a wrong value with an "invalid
 * format" error. "validated: false" means the format is guidance only (the
 * field is not format-checked, but the hint helps when the value is missing).
 */

export interface FieldFormatSpec {
  field: string;
  label: string;
  appliesTo: string;
  format: string;
  example?: string;
  allowed?: string[];
  note?: string;
  validated: boolean;
}

export const FIELD_FORMATS: FieldFormatSpec[] = [
  {
    field: 'DOB',
    label: 'Date of birth',
    appliesTo: 'All countries',
    format: 'A valid date, DD/MM/YYYY. Age must be 18 to 64.',
    example: '15/04/1985',
    note: 'For Cyprus the age range is 16 to 64.',
    validated: true,
  },
  {
    field: 'GENDER',
    label: 'Gender',
    appliesTo: 'All countries',
    format: 'Male or Female.',
    allowed: ['Male', 'Female'],
    validated: true,
  },
  {
    field: 'MARITAL',
    label: 'Marital status',
    appliesTo: 'All countries',
    format: 'One of the configured marital statuses.',
    allowed: ['Single', 'Married', 'Divorced', 'Widowed'],
    validated: true,
  },
  {
    field: 'RELATION',
    label: 'Relation',
    appliesTo: 'All countries',
    format: 'The relationship to the main member.',
    example: 'Self, Spouse, Child',
    note: 'Must match a configured relation value exactly.',
    validated: true,
  },
  {
    field: 'NATIONALITY',
    label: 'Nationality',
    appliesTo: 'Checked for Jordan (Jordanian) and KSA (Saudi)',
    format: 'A configured nationality value.',
    example: 'Jordanian, Saudi',
    note: 'Must match a configured nationality value exactly.',
    validated: true,
  },
  {
    field: 'CLASS',
    label: 'Insurance class',
    appliesTo: 'All countries',
    format: 'A configured insurance category.',
    note: 'Must match a configured category value exactly.',
    validated: true,
  },
  {
    field: 'COSTSHARING',
    label: 'Cost sharing',
    appliesTo: 'Lebanon',
    format: 'Yes or No.',
    allowed: ['Yes', 'No'],
    validated: true,
  },
  {
    field: 'EMAIL',
    label: 'Email',
    appliesTo: 'When provided',
    format: 'A valid email address.',
    example: 'name@company.com',
    validated: true,
  },
  {
    field: 'NATIONALID',
    label: 'National ID',
    appliesTo: 'Jordan, KSA, Kuwait',
    format: '10 digits for Jordan and KSA. 12 digits for Kuwait.',
    validated: true,
  },
  {
    field: 'KID',
    label: 'Kuwait ID',
    appliesTo: 'Kuwait',
    format: '12 digits.',
    validated: true,
  },
  {
    field: 'QID',
    label: 'Qatar ID',
    appliesTo: 'Qatar',
    format: '11 or 12 digits.',
    validated: true,
  },
  {
    field: 'IQAMANUMBER',
    label: 'Iqama number',
    appliesTo: 'KSA, non-Saudi members',
    format: '10 digits.',
    validated: true,
  },
  {
    field: 'IQAMAEXPIRYDATE',
    label: 'Iqama expiry date',
    appliesTo: 'KSA, non-Saudi members',
    format: 'A valid date, DD/MM/YYYY.',
    example: '15/04/2028',
    validated: true,
  },
  {
    field: 'SPONSORID',
    label: 'Sponsor ID',
    appliesTo: 'KSA, non-Saudi members',
    format: '10 digits.',
    validated: true,
  },
  {
    field: 'UIDNUMBER',
    label: 'UID number',
    appliesTo: 'UAE',
    format: '6 to 15 digits.',
    validated: true,
  },
  {
    field: 'EMIRATESID',
    label: 'Emirates ID',
    appliesTo: 'UAE',
    format: '784-XXXX-XXXXXXX-X.',
    example: '784-1985-1234567-8',
    note: 'IDs that do not start with 784 must be 21 characters and start with an approved prefix.',
    validated: true,
  },
  {
    field: 'VISA',
    label: 'Visa number',
    appliesTo: 'UAE, non-Emirati members',
    format: 'XXX/YYYY/XXXXXXX.',
    example: '123/2024/1234567',
    note: 'Sharjah visas use XXX/YYYY/X/XXXXXXX. Emirati members must leave the visa empty.',
    validated: true,
  },
  {
    field: 'VISAISSUANCE',
    label: 'Visa issuance',
    appliesTo: 'UAE',
    format: 'Emirate code and name, written as "N = City".',
    example: '4 = Dubai',
    allowed: ['1 = Sharjah', '4 = Dubai'],
    note: 'Enter the emirate code and name, for example "4 = Dubai". Spacing around "=" is handled automatically. The examples shown are not the full list.',
    validated: true,
  },
  {
    field: 'MEMBERTYPE',
    label: 'Member type',
    appliesTo: 'UAE',
    format:
      'First digit by nationality: 1 = Emirati, 2 = GCC national, 4 = expat with a Dubai visa, 5 = any other expat.',
    example: '5001',
    note: 'Only the first digit is checked. Do not enter the long description text.',
    validated: true,
  },
  {
    field: 'ENTITYTYPE',
    label: 'Entity type',
    appliesTo: 'UAE, non-Emirati members',
    format: 'A configured entity type value.',
    note: 'Must match a configured entity type value exactly.',
    validated: true,
  },
  {
    field: 'HASCOMMISSION',
    label: 'Has commission',
    appliesTo: 'UAE',
    format: 'Yes or No.',
    allowed: ['Yes', 'No'],
    validated: true,
  },
  {
    field: 'INSURED',
    label: 'Insured',
    appliesTo: 'UAE',
    format: 'Yes or No.',
    allowed: ['Yes', 'No'],
    validated: true,
  },
  {
    field: 'RESIDENTIALLOCATION',
    label: 'Residential location',
    appliesTo: 'UAE',
    format: 'Must be 110.',
    example: '110',
    validated: true,
  },
  {
    field: 'WORKLOCATION',
    label: 'Work location',
    appliesTo: 'UAE',
    format: 'Must be 110.',
    example: '110',
    validated: true,
  },
  {
    field: 'SALARYBRACKET',
    label: 'Salary bracket',
    appliesTo: 'UAE',
    format: 'A configured salary bracket value.',
    note: 'Must match a configured salary bracket value exactly.',
    validated: true,
  },
  {
    field: 'COMPANYCODE',
    label: 'Company code',
    appliesTo: 'All countries',
    format: 'A company code that exists in the policy table.',
    note: 'If the code is not found, both the company code and company name are flagged.',
    validated: true,
  },
  {
    field: 'COMPANYNAME',
    label: 'Company name',
    appliesTo: 'All countries',
    format: 'The company name that matches the company code in the policy table.',
    validated: true,
  },
  // Guidance only. These are not format-checked, but the hint helps when the
  // value is missing on a mandatory field.
  {
    field: 'LEAVEDATE',
    label: 'Leave date',
    appliesTo: 'Departures',
    format: 'A date, DD/MM/YYYY.',
    example: '15/04/2026',
    validated: false,
  },
  {
    field: 'MOBILEPHONE',
    label: 'Mobile phone',
    appliesTo: 'When provided',
    format: 'Digits only, no spaces or symbols.',
    example: '71123456',
    validated: false,
  },
  {
    field: 'TIMESET',
    label: 'Employment type',
    appliesTo: 'All countries',
    format: 'Full Time or Part Time.',
    allowed: ['Full Time', 'Part Time'],
    validated: false,
  },
  {
    field: 'USER_PERSON_TYPE',
    label: 'Person type',
    appliesTo: 'All countries',
    format: 'The kind of person on the policy.',
    example: 'Employee, Spouse, Child',
    validated: false,
  },
];

const FIELD_FORMAT_MAP: Record<string, FieldFormatSpec> = FIELD_FORMATS.reduce(
  (acc, spec) => {
    acc[spec.field] = spec;
    return acc;
  },
  {} as Record<string, FieldFormatSpec>
);

export function getFieldFormat(field: string): FieldFormatSpec | undefined {
  return FIELD_FORMAT_MAP[field];
}

/*
 * A short one-line hint for a field, preferring a concrete example. Used for
 * input placeholders and the inline hint in the Fix & Promote dialog.
 */
export function fieldFormatHint(field: string): string {
  const spec = FIELD_FORMAT_MAP[field];
  if (!spec) return '';
  return spec.example ? `e.g. ${spec.example}` : spec.format;
}
